import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, MapPin, BarChart3, TriangleAlert, AlertCircle,
  RefreshCw, ShieldCheck, Database, Info, ChevronLeft, ChevronRight, X, Expand,
} from "lucide-react";
import { getAtlasLiveVehicle } from "../lib/api";
import { Button } from "../components/ui/button";
import { Loading } from "../components/Loading";
import { PriceTag } from "../components/PriceTag";
import { AvailabilityBadge } from "../components/AvailabilityBadge";
import { eur, km } from "../lib/format";

const DataRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-2.5 border-b border-border">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-foreground text-right">{value ?? "—"}</span>
  </div>
);

const SOURCE_LABEL = {
  autoscout24: "AutoScout24", mobile_de: "Mobile.de", "mobile-de": "Mobile.de", otomoto: "Otomoto",
  leboncoin: "Leboncoin", marktplaats: "Marktplaats", lacentrale: "LaCentrale", olx: "OLX",
  finn: "Finn", autokopen: "AutoKopen", kleinanzeigen: "Kleinanzeigen", subito: "Subito",
  coches: "Coches.net", willhaben: "willhaben", instamotion: "Instamotion",
  autotrader: "AutoTrader UK", "autotrader-uk": "AutoTrader UK",
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-display text-lg font-semibold mb-3">{title}</h3>
    {children}
  </div>
);

const POSITION = {
  below: { label: "Below market", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", desc: "priced under comparable listings" },
  around: { label: "Around market", cls: "bg-blue-50 text-blue-700 border-blue-200", desc: "in line with comparable listings" },
  above: { label: "Above market", cls: "bg-amber-50 text-amber-700 border-amber-200", desc: "priced over comparable listings" },
};

const ComparablePanel = ({ cd, populationSize }) => {
  if (!cd) return null;
  const insufficient = cd.status !== "OK";
  const c = cd.criteria || {};
  const dist = cd.sourceDistribution || {};
  const distStr = Object.keys(dist).length
    ? Object.entries(dist).map(([k, v]) => `${k} (${v})`).join(", ")
    : "—";

  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="atlas-comparable-panel"
      style={{ borderTop: "3px solid #2563EB" }}>
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="h-5 w-5 text-atlas-blue" />
        <h3 className="font-display text-lg font-semibold">Market comparison</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Same brand + model within ±{c.yearBand} years{c.yearMin ? ` (${c.yearMin}–${c.yearMax})` : ""}.
        This is a market comparison, <span className="font-medium">not yet an Atlas Opportunity Score</span>.
      </p>

      {insufficient ? (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-4" data-testid="atlas-comparable-insufficient">
          <div className="flex items-center gap-2 text-slate-700 font-medium">
            <Info className="h-4 w-4" /> Insufficient comparables
          </div>
          <p className="text-sm text-slate-600 mt-1.5">{cd.note}</p>
          <div className="mt-3 grid grid-cols-2 gap-y-1 text-xs text-slate-500">
            <span>Comparables found</span><span className="text-right font-mono" data-testid="atlas-comparable-sample">{cd.sampleSize} / need {cd.minSampleSize}</span>
            <span>Confidence</span><span className="text-right">{cd.confidence}</span>
          </div>
        </div>
      ) : (
        <div data-testid="atlas-comparable-ok">
          <div className="flex items-center justify-between mb-4">
            {cd.pricePosition && (
              <span className={`px-3 py-1.5 rounded-md border text-sm font-medium ${POSITION[cd.pricePosition]?.cls}`}
                data-testid="atlas-comparable-position">
                {POSITION[cd.pricePosition]?.label}
              </span>
            )}
            <span className="text-xs text-muted-foreground">{POSITION[cd.pricePosition]?.desc}</span>
          </div>
          <div className="grid grid-cols-2 gap-y-2.5 text-sm">
            <span className="text-muted-foreground">This vehicle</span>
            <span className="text-right font-mono" data-testid="atlas-comparable-target">{eur(cd.targetPriceEur)}</span>
            <span className="text-muted-foreground">Median comparable</span>
            <span className="text-right font-mono" data-testid="atlas-comparable-median">{eur(cd.medianPriceEur)}</span>
            <span className="text-muted-foreground">Range (min–max)</span>
            <span className="text-right font-mono">{eur(cd.minPriceEur)} – {eur(cd.maxPriceEur)}</span>
            <span className="text-muted-foreground">Price percentile</span>
            <span className="text-right font-mono">{cd.pricePercentile != null ? `${cd.pricePercentile}%` : "—"}</span>
            <span className="text-muted-foreground">Comparables</span>
            <span className="text-right font-mono" data-testid="atlas-comparable-sample">{cd.sampleSize}</span>
            <span className="text-muted-foreground">Confidence</span>
            <span className="text-right">{cd.confidence}</span>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
        <div className="flex justify-between"><span>Source distribution</span><span className="text-right" data-testid="atlas-comparable-sources">{distStr}</span></div>
        <div className="flex justify-between mt-1"><span>Store population</span><span>{populationSize}</span></div>
      </div>
    </div>
  );
};

const Lightbox = ({ photos, index, sourceLabel, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);
  const url = photos[index];
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col" data-testid="atlas-gallery-lightbox"
      onClick={onClose}>
      <div className="flex items-center justify-between px-5 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="font-mono text-xs uppercase tracking-wider">Source listing photo · {sourceLabel} · {index + 1}/{photos.length}</span>
        <button onClick={onClose} data-testid="atlas-gallery-close" className="p-2 hover:text-atlas-sky"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-6 relative" onClick={(e) => e.stopPropagation()}>
        {photos.length > 1 && (
          <button onClick={onPrev} data-testid="atlas-gallery-prev"
            className="absolute left-3 md:left-8 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ChevronLeft className="h-6 w-6" /></button>
        )}
        <img src={url} alt={`Photo ${index + 1}`} className="max-h-[80vh] max-w-[90vw] object-contain rounded-md" data-testid="atlas-gallery-image" />
        {photos.length > 1 && (
          <button onClick={onNext} data-testid="atlas-gallery-next"
            className="absolute right-3 md:right-8 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ChevronRight className="h-6 w-6" /></button>
        )}
      </div>
    </div>
  );
};

export default function AtlasVehicleDetail() {
  const { source, id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [comp, setComp] = useState(null);
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroOk, setHeroOk] = useState(true);
  const [galleryIdx, setGalleryIdx] = useState(null);
  const reqRef = useRef(0);

  const load = useCallback(() => {
    const myReq = ++reqRef.current;
    setLoading(true); setError(null); setV(null); setComp(null);
    getAtlasLiveVehicle(source, id)
      .then((resp) => {
        if (myReq !== reqRef.current) return;  // a newer navigation superseded this
        if (resp.status === "NOT_FOUND") { setError({ kind: "notfound" }); return; }
        if (!resp.vehicle) {
          setError({ kind: "unavailable", message: resp.message || "This listing could not be loaded right now." });
          return;
        }
        setV(resp.vehicle);
        setComp({ comparableData: resp.comparableData, populationSize: resp.populationSize });
        setAvail(resp.availability || null);
      })
      .catch((e) => { if (myReq === reqRef.current) setError(e?.response?.status === 404 ? { kind: "notfound" } : { kind: "unavailable" }); })
      .finally(() => { if (myReq === reqRef.current) setLoading(false); });
  }, [source, id]);

  useEffect(() => load(), [load]);

  if (loading) return <Loading label="Compiling the market comparison…" />;

  if (error) {
    const isNotFound = error.kind === "notfound";
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="atlas-detail-error">
        <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
        <h2 className="font-display text-2xl font-bold mt-4">
          {isNotFound ? "Listing not found" : "This listing is temporarily unavailable"}
        </h2>
        {!isNotFound && error.message && (
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{error.message}</p>
        )}
        <div className="flex gap-2 justify-center mt-6">
          {!isNotFound && (
            <Button onClick={load} className="bg-atlas-ink hover:bg-atlas-blue text-white" data-testid="atlas-detail-retry">
              <RefreshCw className="h-4 w-4 mr-2" /> Try again
            </Button>
          )}
          <Link to="/atlas"><Button variant="outline">Back to listings</Button></Link>
        </div>
      </div>
    );
  }

  const s = v.sourceData || {};
  const n = v.normalizedData || {};
  const q = v.qualityData || {};
  const f = v.freshnessData || {};
  const photos = (s.photos || []).filter((p) => p.originalUrl);
  const photoUrls = photos.map((p) => p.originalUrl);
  const hero = photoUrls[0];
  const sourceLabel = (s.sourceSite
    ? (SOURCE_LABEL[s.sourceSite.split(".")[0]] || (s.sourceSite.split(".")[0].charAt(0).toUpperCase() + s.sourceSite.split(".")[0].slice(1)))
    : (SOURCE_LABEL[s.sourceCode] || s.sourceCode));
  const isIntermediary = s.urlType === "intermediary";
  const availState = avail?.state || v.availability?.state || "UNKNOWN";
  const basisLabel = s.sourceCountryBasis === "region" ? "confirmed from listing region"
    : s.sourceCountryBasis === "provider_country" ? "confirmed country"
    : s.sourceCountryBasis === "source_default" ? "source default (region not resolved)" : "unknown";

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8" data-testid="atlas-vehicle-detail">
      {galleryIdx != null && photoUrls.length > 0 && (
        <Lightbox
          photos={photoUrls} index={galleryIdx} sourceLabel={sourceLabel}
          onClose={() => setGalleryIdx(null)}
          onPrev={() => setGalleryIdx((i) => (i - 1 + photoUrls.length) % photoUrls.length)}
          onNext={() => setGalleryIdx((i) => (i + 1) % photoUrls.length)}
        />
      )}
      <button onClick={() => navigate(-1)} data-testid="atlas-detail-back"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div>
          <div className="flex items-center gap-2 text-atlas-blue mb-2">
            <Database className="h-4 w-4" />
            <span className="overline">{s.sourceCode} · genuine listing</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="atlas-detail-title">
            {n.brandDisplay || s.brand} {s.model} {s.trim || ""}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <PriceTag originalPrice={s.originalPrice} originalCurrency={s.originalCurrency} size="lg" testid="atlas-detail-pricetag" priceIsOriginal={s.priceBasis !== "provider_eur"} />
            <AvailabilityBadge state={availState} checkedAt={avail?.checkedAt || s.availabilityCheckedAt || s.lastSeenAt} testid="atlas-detail-availability" />
            <span className="text-slate-300">·</span>
            <span className="flex items-center gap-1 text-muted-foreground"><MapPin className="h-4 w-4" /> {s.sourceCountry || "Location unknown"}{s.region ? ` · ${s.region}` : ""}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="atlas-detail-country-basis">
            Location basis: {basisLabel}
          </p>

          {availState === "UNAVAILABLE" && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-4 py-3" data-testid="atlas-detail-removed-banner">
              <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="text-sm">
                Listing no longer available on {sourceLabel} — the data provider reports it is no longer listed.
                {avail?.checkedAt ? ` Last known ${new Date(avail.checkedAt).toLocaleDateString()}.` : ""}
              </div>
            </div>
          )}
          {availState === "UNKNOWN" && (
            <p className="mt-2 text-[11px] text-muted-foreground" data-testid="atlas-detail-availability-note">
              Availability not verified — the marketplace restricts automated re-checks, so please confirm on the original listing.
            </p>
          )}

          {hero ? (
            <div className="relative mt-6 rounded-md overflow-hidden border border-border h-72 bg-muted cursor-zoom-in group"
              onClick={() => setGalleryIdx(0)} data-testid="atlas-detail-hero">
              {heroOk ? (
                <img src={hero} alt={`${n.brandDisplay || s.brand || ""} ${s.model || ""}`.trim()} className="w-full h-full object-cover"
                  onError={() => setHeroOk(false)} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                  Image unavailable
                </div>
              )}
              <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-black/60 text-white">
                Source listing photo · {sourceLabel}
              </span>
              <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 font-mono text-[10px] px-2 py-1 rounded-sm bg-black/60 text-white">
                <Expand className="h-3 w-3" /> {photos.length} photo{photos.length === 1 ? "" : "s"}
              </span>
            </div>
          ) : (
            <div className="mt-6 rounded-md border border-border h-40 flex items-center justify-center text-muted-foreground" data-testid="atlas-detail-no-photo">Image unavailable — the source did not provide photos</div>
          )}

          {/* Thumbnail strip -> open the gallery at that image */}
          {photoUrls.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="atlas-detail-thumbs">
              {photoUrls.slice(0, 10).map((u, i) => (
                <button key={i} onClick={() => setGalleryIdx(i)} data-testid={`atlas-detail-thumb-${i}`}
                  className="shrink-0 h-16 w-24 rounded-sm overflow-hidden border border-border hover:border-atlas-blue">
                  <img src={u} alt={`Photo ${i + 1}`} className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4 h-fit lg:sticky lg:top-24">
          <ComparablePanel cd={comp?.comparableData} populationSize={comp?.populationSize} />
          <div className="bg-card border border-border rounded-md p-5">
            {s.listingUrl ? (
              <a href={s.listingUrl} target="_blank" rel="noopener noreferrer" data-testid="atlas-detail-original-listing">
                <Button className="w-full bg-atlas-ink hover:bg-atlas-blue text-white">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {isIntermediary ? `View listing (via TheParking)` : `View original listing on ${sourceLabel}`}
                </Button>
              </a>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-2" data-testid="atlas-detail-url-unavailable">
                {isIntermediary ? "Listing URL unavailable" : "Original listing URL unavailable"}
              </div>
            )}
            {s.listingUrl && (
              <p className="text-[11px] text-muted-foreground mt-2 text-center" data-testid="atlas-detail-source-note">
                {isIntermediary
                  ? <>Source: <span className="font-medium text-slate-600">{sourceLabel}</span> · opens via the TheParking aggregator, not the original marketplace.</>
                  : <>If this listing has been removed, the link may no longer work.</>}
              </p>
            )}
            <div className="mt-3 flex items-start gap-2 rounded-md bg-blue-50/60 border border-atlas-blue/20 p-2.5">
              <ShieldCheck className="h-4 w-4 text-atlas-blue shrink-0 mt-0.5" />
              <p className="text-[11px] text-slate-600 leading-snug">
                Atlas aggregates but does not host, sell or handle payment for this listing. Verify all facts with the
                original source. <Link to="/trust" className="text-atlas-blue font-medium">Safety</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-x-12 gap-y-10 mt-12">
        <Section title="Vehicle overview">
          <DataRow label="Year" value={s.year} />
          <DataRow label="Mileage" value={km(n.mileageKm)} />
          <DataRow label="Fuel" value={n.fuelType} />
          <DataRow label="Transmission" value={n.transmission} />
          <DataRow label="Body type" value={n.bodyType} />
          <DataRow label="Engine" value={s.engineCc ? `${s.engineCc} cc` : null} />
          <DataRow label="Drive" value={s.driveType} />
          <DataRow label="Colour" value={s.color} />
        </Section>

        <Section title="Provenance & data quality">
          <DataRow label="Source marketplace" value={sourceLabel} />
          <DataRow label="Listing link type" value={isIntermediary ? "Aggregator (TheParking)" : "Direct marketplace"} />
          <DataRow label="Atlas listing ID" value={v.listingId} />
          <DataRow label="Last verified" value={f.availabilityCheckedAt || f.lastSeenAt || "Unavailable"} />
          <DataRow label="Seller type" value={s.sellerType} />
          <DataRow label="VIN" value={s.vin || null} />
          <DataRow label="Accident status" value={q.accidentStatus} />
          <DataRow label="Completeness" value={q.completenessScore != null ? `${q.completenessScore}%` : null} />
          <DataRow label="Confidence" value={q.confidenceScore != null ? `${q.confidenceScore}%` : null} />
          <DataRow label="Freshness" value={f.freshnessStatus} />
          <div className="mt-4">
            <h4 className="overline mb-2">Warnings</h4>
            <div className="space-y-2">
              {[...(q.warnings || []), ...(f.freshnessWarning ? [f.freshnessWarning] : [])].length ? (
                [...(q.warnings || []), ...(f.freshnessWarning ? [f.freshnessWarning] : [])].map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-amber-700">
                    <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" /> <span>{w}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No warnings.</p>
              )}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
